import React, { useRef, useState, useEffect, useCallback } from "react";

// A floating "water droplet" that magnifies the image behind it.
// Follows the pointer on desktop; drifts on its own when idle / on touch.
export const WaterLens = ({ image, zoom = 2.1, className = "" }) => {
  const ref = useRef(null);
  const raf = useRef(null);
  const hovering = useRef(false);
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [lens, setLens] = useState({ x: 0.62, y: 0.42 }); // normalized 0..1
  const [radius, setRadius] = useState(120);

  useEffect(() => {
    const measure = () => {
      if (!ref.current) return;
      const r = ref.current.getBoundingClientRect();
      setSize({ w: r.width, h: r.height });
      setRadius(r.width < 768 ? 78 : 130);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  // idle drift along a lissajous path
  useEffect(() => {
    let t = 0;
    const loop = () => {
      t += 0.006;
      if (!hovering.current) {
        setLens({ x: 0.5 + 0.28 * Math.sin(t), y: 0.45 + 0.22 * Math.sin(t * 0.8 + 1) });
      }
      raf.current = requestAnimationFrame(loop);
    };
    raf.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf.current);
  }, []);

  const onMove = useCallback((e) => {
    if (!ref.current) return;
    if (window.matchMedia("(pointer: coarse)").matches) return;
    hovering.current = true;
    const r = ref.current.getBoundingClientRect();
    setLens({ x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height });
  }, []);

  const px = lens.x * size.w;
  const py = lens.y * size.h;
  const bgW = size.w * zoom;
  const bgH = size.h * zoom;
  const bgX = -(px * zoom - radius);
  const bgY = -(py * zoom - radius);

  return (
    <div
      ref={ref}
      className={`relative overflow-hidden ${className}`}
      onMouseMove={onMove}
      onMouseLeave={() => (hovering.current = false)}
      data-testid="water-lens"
    >
      <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover" draggable="false" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/25 to-black/70 pointer-events-none" />
      {size.w > 0 && (
        <div
          aria-hidden
          className="absolute rounded-full pointer-events-none will-change-transform"
          style={{
            width: radius * 2,
            height: radius * 2,
            left: px - radius,
            top: py - radius,
            backgroundImage: `url(${image})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: `${bgW}px ${bgH}px`,
            backgroundPosition: `${bgX}px ${bgY}px`,
            boxShadow: "inset 0 8px 24px rgba(255,255,255,0.35), inset 0 -12px 30px rgba(0,0,0,0.45), 0 30px 60px rgba(0,0,0,0.5)",
            border: "1px solid rgba(255,255,255,0.5)",
            transition: "width .3s ease, height .3s ease",
          }}
        >
          <div className="absolute rounded-full" style={{ inset: 0, background: "radial-gradient(120px 90px at 34% 26%, rgba(255,255,255,0.55), rgba(255,255,255,0) 55%)" }} />
          <div className="absolute rounded-full" style={{ inset: 0, boxShadow: "inset 0 0 40px rgba(255,255,255,0.15)" }} />
        </div>
      )}
    </div>
  );
};

export default WaterLens;
